import React, { useState } from 'react';
import {
    LineChart,
    Line,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer
} from 'recharts';

/**
 * 司機績效圖表組件
 * @param {Object} props
 * @param {Array} props.data - 圖表數據
 * @param {string} props.type - 圖表類型: 'line' 或 'bar'
 * @param {string} props.dataKey - 數據鍵名
 * @param {string} props.xAxisKey - X軸鍵名
 * @param {string} props.name - 數據名稱
 * @param {string} props.color - 圖表顏色
 * @param {Array} props.metrics - 多個指標時使用
 */
const DriverPerformanceChart = ({
    data = [],
    type = 'line',
    dataKey = 'value',
    xAxisKey = 'date',
    name = '績效',
    color = '#3B82F6',
    metrics = []
}) => {
    const [chartType, setChartType] = useState(type);

    // 圖表頁籤選項
    const chartTypeOptions = [
        { value: 'line', label: '折線圖' },
        { value: 'bar', label: '柱狀圖' }
    ];

    // 渲染圖表內容
    const renderChart = () => {
        if (chartType === 'line') {
            return (
                <LineChart data={data}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey={xAxisKey} />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    {metrics.length > 0 ? (
                        // 多指標
                        metrics.map((metric, index) => (
                            <Line
                                key={metric.key}
                                type="monotone"
                                dataKey={metric.key}
                                name={metric.name}
                                stroke={metric.color || `hsl(${index * 30}, 70%, 50%)`}
                                activeDot={{ r: 8 }}
                            />
                        ))
                    ) : (
                        // 單一指標
                        <Line
                            type="monotone"
                            dataKey={dataKey}
                            name={name}
                            stroke={color}
                            activeDot={{ r: 8 }}
                        />
                    )}
                </LineChart>
            );
        } else if (chartType === 'bar') {
            return (
                <BarChart data={data}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey={xAxisKey} />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    {metrics.length > 0 ? (
                        // 多指標
                        metrics.map((metric, index) => (
                            <Bar
                                key={metric.key}
                                dataKey={metric.key}
                                name={metric.name}
                                fill={metric.color || `hsl(${index * 30}, 70%, 50%)`}
                            />
                        ))
                    ) : (
                        // 單一指標
                        <Bar dataKey={dataKey} name={name} fill={color} />
                    )}
                </BarChart>
            );
        }

        return null;
    };

    if (!data || data.length === 0) {
        return (
            <div className="flex items-center justify-center h-64 bg-gray-50 rounded-lg">
                <p className="text-gray-500">無數據</p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* 圖表類型切換 */}
            <div className="flex">
                {chartTypeOptions.map(option => (
                    <button
                        key={option.value}
                        className={`px-4 py-2 text-sm font-medium ${chartType === option.value
                                ? 'bg-blue-100 text-blue-600 rounded-md'
                                : 'text-gray-600 hover:bg-gray-100 rounded-md'
                            }`}
                        onClick={() => setChartType(option.value)}
                    >
                        {option.label}
                    </button>
                ))}
            </div>

            {/* 圖表 */}
            <div className="h-96">
                <ResponsiveContainer width="100%" height="100%">
                    {renderChart()}
                </ResponsiveContainer>
            </div>
        </div>
    );
};

export default DriverPerformanceChart;